import Model from '@/common/models/common.model';
import { store } from '@/common/root';
import {
  COMPUTE_NODE_STATUSES,
  COMPUTE_NODE_STATUS_MAP,
  COMPUTE_ROLE
} from '@/common/constants/computeNodes.constants';
import { ComputeNode as ComputeNodeSchema } from '@schema';
import Server from '@/common/models/server.model';
import Node from '@/common/models/node.model';
import { NODES_STATUSES_V3 } from '@/common/constants/nodes.constants';
import { COMPUTE_NODE_SERVICE_TAG_TYPE } from '@/common/constants/services.constants';

export default class ComputeNode extends Model {
  declare attributes: ComputeNodeSchema;

  get tagTypeMap() {
    return COMPUTE_NODE_SERVICE_TAG_TYPE;
  }

  get statusIcon() {
    return COMPUTE_NODE_STATUS_MAP[this.status].icon;
  }

  get statusText() {
    return COMPUTE_NODE_STATUS_MAP[this.status].text;
  }

  get ip() {
    return this.attributes.host_ip || '';
  }

  get cpuModel() {
    return this.attributes.cpu_model || this.attributes.hypervisor?.cpu_info?.model || '';
  }

  get name() {
    return this.node?.name || this.hostName;
  }

  get hostName() {
    return this.attributes.host || '';
  }

  get isFenced(): boolean {
    return this.hypervisor?.status === 'disabled';
  }

  get fencedReason() {
    return this.attributes.fenced_reason;
  }

  get isInMaintenance(): boolean | undefined {
    return this.attributes.in_maintenance;
  }

  get status(): COMPUTE_NODE_STATUSES | ComputeNodeSchema['state'] {
    const { state } = this.attributes;
    if (!this.isOnline || !state) {
      return COMPUTE_NODE_STATUSES.OFFLINE;
    }

    if (this.node.status === NODES_STATUSES_V3.RECOVERING) {
      return COMPUTE_NODE_STATUSES.RECOVERING;
    }
    if (this.node.status === NODES_STATUSES_V3.RECOVERY_FAILED) {
      return COMPUTE_NODE_STATUSES.RECOVERY_FAILED;
    }
    if (this.isInMaintenance) {
      return COMPUTE_NODE_STATUSES.MAINTENANCE;
    }
    return state;
  }

  get isOnline() {
    return this.node.isOnline;
  }

  get node(): Node {
    return store.getters['nodes/getItemById']?.(this.id) || {};
  }

  get servers(): Server {
    return store.getters['servers/getItemsByProp'] ? store.getters['servers/getItemsByProp']('hostName')(this.hostName) : {};
  }

  get isDeletable(): boolean {
    // it's not allowed to remove compute node while there are VM hosted on it
    // U9 - Compute cluster destroy via WebCP
    // https://adn.acronis.com/display/ADCA/Compute+Cluster+management
    return !this.vms && !this.isLoading && !this.isInRecovery;
  }

  get isLoading(): boolean {
    return this.status === COMPUTE_NODE_STATUSES.JOINING || this.status === COMPUTE_NODE_STATUSES.RELEASING;
  }

  get isManagement(): boolean {
    // Management node is node from HA and is_primary
    return this.node?.isManagement;
  }

  get isInRecovery(): boolean {
    return this.node?.isInRecovery;
  }

  // Statistics
  get physicalStats(): any {
    return this.attributes.statistics?.physical || {};
  }

  get reservedStats(): any {
    return this.attributes.statistics?.reserved || {};
  }

  get computeStats(): any {
    return this.attributes.statistics?.compute || {};
  }

  get vcpus() {
    return this.computeStats.vcpus || 0;
  }

  get cpusCores() {
    return this.physicalStats?.cpu_cores || 0;
  }

  // Number or vcpu usage in percents
  get cpuUsage() {
    return this.physicalStats?.cpu_usage || 0;
  }

  get cpuUsedByAll() {
    return ((this.cpusCores * this.cpuUsage) / 100) || 0;
  }

  get cpuUsedByVMs() {
    return this.computeStats.vcpus || 0;
  }

  get cpuUsedBySystem() {
    return this.reservedStats.vcpus || 0;
  }

  get freeCpus() {
    return this.computeStats.vcpus_free || 0;
  }

  get cpuUsageDecimal() {
    return (this.cpusCores * this.cpuUsage) / 100 || 0;
  }

  // Number or vcpu usage in percents
  get vcpusUsedPercent() {
    return this.attributes.statistics?.compute?.cpu_usage || 0;
  }

  // Number or vcpu usage
  get vcpusUsedDecimal() {
    return (this.vcpus * this.vcpusUsedPercent) / 100 || 0;
  }

  get vcpusProvisioned() {
    return this.attributes.statistics?.reserved?.cpus || 0;
  }

  get ram() {
    return this.attributes.statistics?.compute?.vm_mem_reserved || 0;
  }

  get ramPhysical() {
    return this.attributes.statistics?.physical?.mem_total || 0;
  }

  get ramPhysicalFree() {
    return this.attributes.statistics?.physical?.mem_free || 0;
  }

  get ramPhysicalUsed() {
    return (this.ramPhysical - this.ramPhysicalFree) || 0;
  }

  get ramProvisioned() {
    return (this.attributes.statistics?.reserved?.memory || 0) * 1024;
  }

  get ramUsed() {
    return this.attributes.statistics?.compute?.vm_mem_usage || 0;
  }

  get ramUsedByVMs() {
    return this.computeStats.vm_mem_usage || 0;
  }

  get ramReservedForSystem() {
    return this.reservedStats.memory || 0;
  }

  get ramReservedForVMs() {
    return this.computeStats.vm_mem_reserved || 0;
  }

  get ramUsedByAll() {
    return this.ramReservedForSystem + this.computeStats.vm_mem_capacity;
  }

  get ramFree() {
    return this.computeStats.vm_mem_free || 0;
  }

  get ramTotal() {
    return this.physicalStats.mem_total || 0;
  }

  get swapTotal() {
    return this.physicalStats.swap_total || 0;
  }

  get swapUsed() {
    return this.swapTotal - (this.physicalStats.swap_free || 0);
  }

  get stats() {
    return this.attributes.statistics || {};
  }

  get hypervisor() {
    return this.attributes.hypervisor;
  }

  get vms(): number {
    return this.hypervisor?.vms || 0;
  }

  get hypervisorType() {
    return this.hypervisor?.hypervisor_type;
  }

  get traits() {
    return this.attributes.traits || [];
  }

  get computeNodeServices() {
    return this.attributes.services;
  }

  get isInHA() {
    return this.node.isInHA;
  }

  get isAvailable() {
    return this.node.isAvailable;
  }

  get isPrimary() {
    return this.node.isPrimary;
  }

  get services() {
    return this.node.services;
  }

  get roles() {
    return this.attributes.roles || [];
  }

  get isComputeWorker() {
    return this.roles.includes(COMPUTE_ROLE.compute);
  }

  get customParams() {
    return this.attributes.custom_params || [];
  }
}
